/*
  Brandkit (pakket B), 14 okt 2026: menulade (snippets/aa-menu-drawer.liquid).
  Eigen bestand; global.js (Dawn MenuDrawer/HeaderDrawer) is niet gewijzigd en wordt hier niet gebruikt.

  - <dialog>.showModal(): bovenste laag, focus blijft in de lade, de rest van de pagina is inert.
  - Sluiten: kruis, Esc (cancel), klik op de overlay (buiten het paneel), of een link volgen.
  - Submenu's: panelen met terugknop; focus gaat naar de terugknop en terug naar de opener.
  - Pagina scrolt niet mee (klasse op <html>), en bij < 750 de bestaande body-klasse aa-mobile-drawer-open.
  - Animaties volgen --aa-dur-drawer; bij reduced motion (0s) sluit hij direct.
*/
if (!customElements.get('aa-menu-drawer')) {
  customElements.define(
    'aa-menu-drawer',
    class AaMenuDrawer extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        this.toggle = this.querySelector('[data-aa-menu-open]');
        if (!this.dialog || !this.toggle) return;

        this.onToggle = () => (this.dialog.open ? this.close() : this.open());
        this.onCancel = (event) => {
          event.preventDefault();
          this.close();
        };
        this.onDialogClick = (event) => {
          // Klik op de ::backdrop komt binnen als klik op de dialog zelf, buiten het paneel
          if (event.target !== this.dialog) return;
          const rect = this.panel.getBoundingClientRect();
          const inside =
            event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
          if (!inside) this.close();
        };
        this.onPanelClick = (event) => {
          const opener = event.target.closest('[data-aa-submenu-open]');
          if (opener) return this.showLevel(opener.getAttribute('data-aa-submenu-open'), opener);
          const back = event.target.closest('[data-aa-submenu-back]');
          if (back) return this.goBack(back.closest('[data-aa-level]'));
          if (event.target.closest('[data-aa-menu-close]')) return this.close();
          const link = event.target.closest('a[href]');
          if (link && !event.defaultPrevented && !link.target) this.close({ immediate: true, restoreFocus: false });
        };
        this.onResize = () => {
          if (this.dialog.open) this.syncBodyClass(true);
        };

        this.panel = this.dialog.querySelector('.aa-menu__panel');
        this.body = this.dialog.querySelector('.aa-menu__body');
        this.root = this.dialog.querySelector('[data-aa-level="root"]');

        this.toggle.addEventListener('click', this.onToggle);
        this.dialog.addEventListener('cancel', this.onCancel);
        this.dialog.addEventListener('click', this.onDialogClick);
        this.panel.addEventListener('click', this.onPanelClick);
        window.addEventListener('resize', this.onResize);
      }

      disconnectedCallback() {
        if (!this.toggle) return;
        this.toggle.removeEventListener('click', this.onToggle);
        this.dialog.removeEventListener('cancel', this.onCancel);
        this.dialog.removeEventListener('click', this.onDialogClick);
        this.panel.removeEventListener('click', this.onPanelClick);
        window.removeEventListener('resize', this.onResize);
        this.unlockScroll();
      }

      duration() {
        const value = getComputedStyle(this.dialog).getPropertyValue('--aa-dur-drawer').trim();
        const seconds = parseFloat(value) || 0;
        return value.endsWith('ms') ? seconds : seconds * 1000;
      }

      open() {
        if (this.dialog.open) return;
        clearTimeout(this.closeTimer);
        this.resetLevels();
        this.dialog.classList.remove('is-closing');
        this.dialog.showModal();
        // Volgende frame: zo loopt de inschuif-overgang ook na display:none
        requestAnimationFrame(() => this.dialog.classList.add('is-open'));
        this.toggle.setAttribute('aria-expanded', 'true');
        this.toggle.setAttribute('aria-label', this.toggle.dataset.labelClose || 'Menu sluiten');
        this.lockScroll();
        const first = this.dialog.querySelector('[data-aa-menu-close]');
        if (first) first.focus();
      }

      close({ immediate = false, restoreFocus = true } = {}) {
        if (!this.dialog.open) return;
        this.toggle.setAttribute('aria-expanded', 'false');
        this.toggle.setAttribute('aria-label', this.toggle.dataset.labelOpen || 'Menu openen');
        this.dialog.classList.remove('is-open');
        this.dialog.classList.add('is-closing');
        const finish = () => {
          clearTimeout(this.closeTimer);
          if (!this.dialog.open) return;
          this.dialog.close();
          this.dialog.classList.remove('is-closing');
          this.unlockScroll();
          this.resetLevels();
          if (restoreFocus) this.toggle.focus();
        };
        const ms = immediate ? 0 : this.duration();
        if (ms > 0) this.closeTimer = setTimeout(finish, ms);
        else finish();
      }

      showLevel(id, opener) {
        const target = this.dialog.querySelector(`[data-aa-level="${id}"]`);
        if (!target) return;
        const current = opener.closest('[data-aa-level]');
        if (current) {
          current.hidden = true;
        }
        target.hidden = false;
        this.lastOpener = this.lastOpener || {};
        this.lastOpener[id] = opener;
        opener.setAttribute('aria-expanded', 'true');
        this.body.scrollTop = 0;
        const back = target.querySelector('[data-aa-submenu-back]');
        if (back) back.focus();
      }

      goBack(level) {
        if (!level) return;
        const backId = level.getAttribute('data-aa-back');
        const target = this.dialog.querySelector(`[data-aa-level="${backId}"]`);
        level.hidden = true;
        if (target) target.hidden = false;
        const opener = this.lastOpener && this.lastOpener[level.getAttribute('data-aa-level')];
        if (opener) {
          opener.setAttribute('aria-expanded', 'false');
          opener.focus();
        }
      }

      resetLevels() {
        this.dialog.querySelectorAll('[data-aa-level]').forEach((level) => {
          level.hidden = level !== this.root;
        });
        this.dialog.querySelectorAll('[data-aa-submenu-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
        if (this.body) this.body.scrollTop = 0;
      }

      lockScroll() {
        document.documentElement.classList.add('aa-menu-open');
        this.syncBodyClass(true);
      }

      unlockScroll() {
        document.documentElement.classList.remove('aa-menu-open');
        this.syncBodyClass(false);
      }

      syncBodyClass(open) {
        // Bestaande klasse uit het oude ladescript; andere onderdelen kunnen erop leunen
        document.body.classList.toggle('aa-mobile-drawer-open', open && window.innerWidth <= 749);
      }
    }
  );
}
